import { createMemo, For, Show } from "solid-js";
import { verificationStateLabel } from "./clientLanguage";
import { clientDisplayText, presentEvidence } from "./evidencePresentation";
import {
  type PageAssessment,
  type PageContentFitness,
  pageContentFitnessDimensions,
} from "./pageContentFitnessModel";

export function ContentFitnessRationale(props: {
  contentFitness: PageContentFitness;
}) {
  const dimensions = createMemo(() =>
    pageContentFitnessDimensions(props.contentFitness),
  );
  const assessmentCount = () =>
    dimensions().reduce(
      (count, dimension) => count + dimension.assessments.length,
      0,
    );
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Page findings and rationale</span>
          <h2>Why this page scored this way</h2>
          <p class="scan-muted">
            Each rationale sits with the subcriterion it assesses. Supporting
            evidence is available one level deeper.
          </p>
        </div>
      </div>
      <Show
        when={assessmentCount() > 0}
        fallback={
          <p class="scan-muted">
            No Content Fitness subcriterion assessments are available for this
            page.
          </p>
        }
      >
        <div class="scan-page-assessment-groups">
          <For each={dimensions()}>
            {(dimension) => (
              <Show when={dimension.assessments.length > 0}>
                <section class="scan-page-assessment-group">
                  <h3>{dimension.label}</h3>
                  <div class="scan-assessment-list">
                    <For each={dimension.assessments}>
                      {(assessment) => (
                        <AssessmentDetail assessment={assessment} />
                      )}
                    </For>
                  </div>
                </section>
              </Show>
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function AssessmentDetail(props: { assessment: PageAssessment }) {
  const evidence = () => presentEvidence(props.assessment.evidence);
  const technicalCount = () => evidence().technical.length;
  return (
    <article class="scan-assessment-row">
      <div>
        <strong>{props.assessment.label}</strong>
      </div>
      <div class="scan-assessment-score">
        <strong class="mono">
          {props.assessment.score === null
            ? "--"
            : `${props.assessment.score}/4`}
        </strong>
        <small>
          {verificationStateLabel(props.assessment.verificationState)}
        </small>
      </div>
      <p>{clientDisplayText(props.assessment.rationale)}</p>
      <Show when={evidence().readable.length > 0}>
        <details class="scan-inline-disclosure">
          <summary>
            Evidence for {props.assessment.label} ({evidence().readable.length})
          </summary>
          <ul class="scan-evidence-list">
            <For each={evidence().readable}>
              {(evidence) => (
                <li class="scan-evidence-card">
                  <p>{evidence.value}</p>
                </li>
              )}
            </For>
          </ul>
          <Show when={technicalCount() > 0}>
            <details class="scan-technical-disclosure">
              <summary>Technical details ({technicalCount()})</summary>
              <TechnicalEvidence items={evidence().technical} />
            </details>
          </Show>
        </details>
      </Show>
      <Show when={evidence().readable.length === 0 && technicalCount() > 0}>
        <details class="scan-inline-disclosure scan-technical-disclosure">
          <summary>Technical evidence ({technicalCount()})</summary>
          <TechnicalEvidence items={evidence().technical} />
        </details>
      </Show>
    </article>
  );
}

function TechnicalEvidence(props: {
  items: ReturnType<typeof presentEvidence>["technical"];
}) {
  return (
    <div class="scan-disclosure-body">
      <ul class="scan-evidence-list mono">
        <For each={props.items}>
          {(item) => <li class="scan-evidence-card mono">{item.value}</li>}
        </For>
      </ul>
    </div>
  );
}
